import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronLeft, ChevronRight, History } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export const SalesWeekPicker = ({ value, onChange, refreshKey = 0 }) => {
  const { getAuthHeader } = useAuth();
  const [weeks, setWeeks] = useState([]);

  useEffect(() => {
    axios.get(`${API}/sales/admin/weeks`, getAuthHeader())
      .then((res) => setWeeks(res.data.weeks || []))
      .catch(() => setWeeks([]));
  }, [getAuthHeader, refreshKey]);

  if (weeks.length === 0) return null;
  const current = weeks.find((w) => w.is_current)?.week_start || weeks[0].week_start;
  const selected = value || current;
  const idx = weeks.findIndex((w) => w.week_start === selected);
  const older = weeks[idx + 1];
  const newer = idx > 0 ? weeks[idx - 1] : null;
  const pick = (ws) => onChange(ws === current ? null : ws);

  return (
    <div className="flex items-center gap-1 rounded-full border border-white/15 bg-white/5 p-1" data-testid="sales-week-picker">
      <button
        type="button"
        onClick={() => older && pick(older.week_start)}
        disabled={!older}
        className="flex h-7 w-7 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/15 hover:text-white disabled:opacity-30"
        aria-label="Previous sales week"
        data-testid="sales-week-prev"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <Select value={selected} onValueChange={pick}>
        <SelectTrigger
          className="h-7 w-auto gap-1.5 border-0 bg-transparent px-2 text-xs font-bold text-white shadow-none focus:ring-0"
          data-testid="sales-week-select"
        >
          <History className="h-3.5 w-3.5 text-white/60" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {weeks.map((w) => (
            <SelectItem key={w.week_start} value={w.week_start} data-testid={`sales-week-option-${w.week_start}`}>
              {fmt(w.week_start)} – {fmt(w.week_end)}
              {w.is_current ? ' · This week' : ` · ${w.policies} ${w.policies === 1 ? 'policy' : 'policies'}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        type="button"
        onClick={() => newer && pick(newer.week_start)}
        disabled={!newer}
        className="flex h-7 w-7 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/15 hover:text-white disabled:opacity-30"
        aria-label="Next sales week"
        data-testid="sales-week-next"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};
